import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import { useTranslation, Trans } from "react-i18next";
import PeriodPicker from "components/common/PeriodPicker";

const SESSION_COLORS = {
  training: "#E07A3A",
  match: "#C07030",
};

const AttendanceBarChart = ({
  sessions,
  sessionTypeFilter,
  onSessionTypeChange,
  period,
  setPeriod,
  totalPlayers,
}) => {
  const { t } = useTranslation();
  const filteredSessions =
    sessionTypeFilter === "all"
      ? sessions
      : sessions.filter(
          (s) =>
            s.type === sessionTypeFilter || s.session_type === sessionTypeFilter
        );

  const chartData = filteredSessions.map((s) => ({
    name: s.shortLabel || s.name || "",
    sessionName: s.name || s.session_name || "",
    type: s.type || s.session_type || "training",
    presentCount: s.presentCount,
    totalPlayers: s.totalPlayers,
    rate:
      s.totalPlayers > 0
        ? Math.round((s.presentCount / s.totalPlayers) * 100)
        : 0,
    label: `${s.presentCount}/${s.totalPlayers}`,
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      const isMatch = data?.type === "match";
      return (
        <div className="min-w-[140px] rounded-lg border border-[#F3F4F6] bg-white px-3 py-2.5 shadow-lg">
          <p className="mb-1 text-[13px] font-semibold text-[#111827]">
            {data.sessionName}
          </p>
          <div className="flex items-center gap-2">
            <span
              className="rounded px-1.5 py-0.5 text-[10px] font-medium"
              style={{
                backgroundColor: isMatch ? "#FEF3E7" : "#FFF3ED",
                color: isMatch ? "#C07030" : "#E07A3A",
              }}
            >
              {isMatch
                ? t("team.sessionTypes.match")
                : t("team.sessionTypes.training")}
            </span>
          </div>
          <div className="mt-2 border-t border-[#F3F4F6] pt-2">
            <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
              {t("team.attendanceChart.tooltipTitle")}
            </p>
            <p className="text-lg font-bold text-[#111827]">
              <Trans
                i18nKey="team.attendanceChart.tooltipCount"
                values={{
                  present: data.presentCount,
                  total: data.totalPlayers,
                }}
                components={{
                  muted: (
                    <span className="text-xs font-normal text-[#6B7280]" />
                  ),
                }}
              />
            </p>
            <p className="text-[11px] text-[#6B7280]">
              {t("team.attendanceChart.turnout", { rate: data.rate })}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderBarLabel = (props) => {
    const { x, y, width, value } = props;
    if (!value) return null;
    return (
      <text
        x={x + width / 2}
        y={y - 6}
        fill="#6B7280"
        textAnchor="middle"
        fontSize={9}
        fontWeight={500}
      >
        {value}
      </text>
    );
  };

  return (
    <div className="rounded-xl border border-[#F3F4F6] bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
        <h3 className="text-sm font-semibold text-[#111827]">
          {t("team.attendanceChart.title")}
        </h3>
        <div className="flex items-center gap-2">
          {/* Session type filter */}
          <div className="flex rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-0.5">
            {[
              { value: "all", label: t("team.sessionTypes.all") },
              { value: "training", label: t("team.sessionTypes.training") },
              { value: "match", label: t("team.sessionTypes.match") },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => onSessionTypeChange(opt.value)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition duration-150 ${
                  sessionTypeFilter === opt.value
                    ? "bg-white text-[#111827] shadow-sm"
                    : "text-[#6B7280] hover:text-[#374151]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <PeriodPicker value={period} onChange={setPeriod} />
        </div>
      </div>

      {/* Chart */}
      <div className="p-4">
        {chartData.length === 0 ? (
          <div className="flex h-[200px] flex-col items-center justify-center text-center">
            <p className="text-sm font-semibold text-[#111827]">
              {t("team.attendanceChart.emptyTitle")}
            </p>
            <p className="mt-1 text-xs text-[#6B7280]">
              {t("team.attendanceChart.emptyBody")}
            </p>
          </div>
        ) : (
          <>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 5, left: -20, bottom: 5 }}
                  barCategoryGap="20%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#F3F4F6"
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: "#9CA3AF" }}
                    dy={8}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: "#9CA3AF" }}
                    domain={[0, totalPlayers || "auto"]}
                    dx={-5}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "#F9FAFB" }}
                  />
                  <Bar
                    dataKey="presentCount"
                    name={t("team.attendanceChart.present")}
                    radius={[3, 3, 0, 0]}
                    maxBarSize={40}
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          SESSION_COLORS[entry.type] || SESSION_COLORS.training
                        }
                      />
                    ))}
                    <LabelList dataKey="label" content={renderBarLabel} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Legend */}
            <div className="mt-2 flex items-center justify-end gap-4">
              <div className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded bg-[#E07A3A]" />
                <span className="text-xs text-[#6B7280]">
                  {t("team.sessionTypes.training")}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded bg-[#C07030]" />
                <span className="text-xs text-[#6B7280]">
                  {t("team.sessionTypes.match")}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AttendanceBarChart;
